import { useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'ui/Button';
import Drawer from 'ui/Drawer';
import { Text } from 'ui/Typography';
import { hasModifiedForm } from 'utils/form';

import { TeamSettings, useTeamContext } from '../../utils/team';
import { useCurrentUser } from '../../utils/user';
import UnsavedChangesAlertDialog from '../UnsavedChangesAlertDialog';
import CorporateBrandingSettingsContent from './CorporateBrandingSettingsContent';
import CreateBrandingIntro from './CreateBrandingIntro';
import NoAccessInfo from './NoAccessInfo';
import SignUpToAddBrandingInfo from './SignUpToAddBrandingInfo';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const CorporateBrandingDrawer: React.FC<Props> = ({ open, setOpen }) => {
  const { user } = useCurrentUser();
  const { hasInitialized, teamSettings, saveSettings, isSaving } =
    useTeamContext();

  const isTeamAdmin = user?.default_team?.is_team_admin;

  const form = useForm<TeamSettings>({ defaultValues: teamSettings });
  const hasUnsavedChanges = hasModifiedForm(form);

  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <UnsavedChangesAlertDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        discardChanges={() => {
          // Reset form to the last saved state and close the drawer
          form.reset(teamSettings);
          setConfirmOpen(false);
          setOpen(false);
        }}
      />
      <Drawer.Root
        open={open}
        onOpenChange={(e) => {
          if (hasUnsavedChanges && !e.open) {
            setConfirmOpen(true);
          } else {
            setOpen(e.open);
          }
        }}
        size="sm"
      >
        <Drawer.Content>
          <Drawer.Header>
            <Drawer.Title>Corporate branding</Drawer.Title>
          </Drawer.Header>
          <Drawer.Body>
            {hasInitialized && (
              <Text color="gray.600" fontSize="sm" mt={-3} mb={8}>
                Configure your branding and make it readily available to all
                your organization members.
              </Text>
            )}
            {!user ? (
              <SignUpToAddBrandingInfo />
            ) : !isTeamAdmin ? (
              <NoAccessInfo />
            ) : !hasInitialized ? (
              <CreateBrandingIntro />
            ) : (
              <CorporateBrandingSettingsContent form={form} />
            )}
          </Drawer.Body>
          <Drawer.Footer>
            {hasInitialized && (
              <Button
                variant="primary-solid"
                disabled={!hasUnsavedChanges}
                onClick={form.handleSubmit((settings) => {
                  saveSettings(settings);
                  form.reset(settings); // Reset form to clear dirty state
                })}
                loading={isSaving}
                w="100%"
              >
                Save changes
              </Button>
            )}
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Root>
    </>
  );
};

export default CorporateBrandingDrawer;
